import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  Stack,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Api,
  BarChart,
  AccountBalanceWallet,
  HelpOutline,
} from '@mui/icons-material';

const StyledCard = styled(Card)(() => ({
  borderRadius: '12px',
  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
  border: '1px solid #f1f5f9',
  transition: 'all 0.2s ease-in-out',
  cursor: 'pointer',
  '&:hover': {
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    transform: 'translateY(-1px)',
  },
}));

const QuickAccessItem = ({ title, description, count, icon, color, onClick }) => {
  return (
    <StyledCard onClick={onClick}>
      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar
            sx={{
              bgcolor: `${color}15`,
              color: color,
              width: 40,
              height: 40,
              '& .MuiSvgIcon-root': {
                fontSize: '2.25rem',
              },
            }}
          >
            {icon}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: '#1e293b',
                  fontSize: '16px',
                  mb: 0.5,
                }}
              >
                {title}
              </Typography>
              {count && (
                <Chip
                  label={count}
                  size="small"
                  sx={{
                    backgroundColor: `${color}15`,
                    color: color,
                    fontSize: '12px',
                    fontWeight: 600,
                  }}
                />
              )}
            </Stack>
            <Typography
              variant="body2"
              sx={{
                color: '#64748b',
                fontSize: '14px',
              }}
            >
              {description}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </StyledCard>
  );
};

export const QuickAccess = ({ onNavigate, totalApis: totalApisCount, remainingCredits: remainingCreditsCount }) => {
  const [totalApis, setTotalApis] = useState(`${totalApisCount || 0} APIs`);
  const [remainingCredits, setRemainingCredits] = useState(`${remainingCreditsCount || 0} Available`);

  useEffect(() => {
    if (totalApisCount !== undefined) {
      setTotalApis(`${totalApisCount} APIs`);
    }
  }, [totalApisCount]);

  useEffect(() => {
    if (remainingCreditsCount !== undefined) {
      setRemainingCredits(`${parseFloat(remainingCreditsCount).toFixed(1)} Available`);
    }
  }, [remainingCreditsCount]);

  const handleNavigation = (path) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    onNavigate?.(path);
  };

  const quickAccessItems = [
    {
      title: 'API Catalogue',
      description: 'Browse and subscribe to APIs',
      count: totalApis,
      icon: <Api />,
      color: '#6366f1',
      onClick: () => handleNavigation('/api-catalogue'),
    },
    {
      title: 'Usage Reports',
      description: 'View API consumption analytics',
      count: 'This Month',
      icon: <BarChart />,
      color: '#10b981',
      onClick: () => handleNavigation('/usage-reports'),
    },
    {
      title: 'Credit Management',
      description: 'Manage your API credits',
      count: remainingCredits,
      icon: <AccountBalanceWallet />,
      color: '#f59e0b',
      onClick: () => handleNavigation('/credit-balance'),
    },
    {
      title: 'Support Center',
      description: 'Get help and raise tickets',
      count: '24/7 Support',
      icon: <HelpOutline />,
      color: '#8b5cf6',
      onClick: () => handleNavigation('/contact-us'),
    },
  ];

  return (
    <Card sx={{ borderRadius: '12px', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)', border: '1px solid #f1f5f9' }}>
      <CardContent sx={{ p: 3 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: '#1e293b',
            mb: 3,
            fontSize: '20px',
          }}
        >
          Quick Access
        </Typography>
        <Grid container spacing={2}>
          {quickAccessItems.map((item, index) => (
            <Grid item xs={12} key={index}>
              <QuickAccessItem {...item} />
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
};
